// src/App.jsx
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { GuestProvider } from './context/GuestContext';

// --- Layouts y Componentes de Protección ---
import MainLayout from './components/MainLayout';
import PatientLayout from './components/PatientLayout';
import ProtectedRoute from './components/ProtectedRoute';
import PatientProtectedRoute from './components/PatientProtectedRoute';
import SmartBookingRoute from './components/SmartBookingRoute';

// --- Páginas Públicas ---
import LandingPage from './pages/LandingPage';
import MockupGenerator from './utils/MockupGenerator';
import Login from './pages/Login';
import RegisterPage from './pages/RegisterPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import GoogleAuthCallback from './pages/GoogleAuthCallback';
import PatientLoginPage from './pages/PatientLoginPage';
import PatientRegisterPage from './pages/PatientRegisterPage';
import PatientJoinPage from './pages/PatientJoinPage';
import ThankYouPage from './pages/ThankYouPage';
import PublicBookingPage from './pages/PublicBookingPage';
import ConfirmBookingPage from './pages/ConfirmBookingPage';
import ClinicJoinPage from './pages/Clinic/ClinicJoinPage';

// --- Páginas de Médicos ---
import DashboardPage from './pages/DashboardPage';
import CitasPage from './pages/CitasPage';
import EditCita from './pages/EditCita';
import ConfiguracionRecordatorios from './pages/ConfiguracionRecordatorios';
import PacientesPage from './pages/PacientesPage';
import PacienteDetailPage from './pages/PacienteDetailPage';
import GrowthChartsPage from './pages/GrowthChartsPage';
import EditPaciente from './pages/EditPaciente';
import AddConsultaPage from './pages/AddConsultaPage';
import ConsultaDetailPage from './pages/ConsultaDetailPage';
import TelemedicinePage from './pages/TelemedicinePage';
import MedicoProfilePage from './pages/MedicoProfilePage';
import FinanzasPage from './pages/FinanzasPage';
import CatalogoPage from './pages/CatalogoPage';
import MedicamentoCatalogoPage from './pages/MedicamentoCatalogoPage';
import AyudaPage from './pages/AyudaPage';
import AcercaDePage from './pages/AcercaDePage';
import PlanesPage from './pages/PlanesPage';
import SuscripcionPage from './pages/SuscripcionPage';
import DisponibilidadConfig from './components/DisponibilidadConfig';
import DoctorReviewsModerationPage from './pages/DoctorReviewsModerationPage';
import ClinicMembersPage from './pages/Clinic/ClinicMembersPage';
import ClinicModeRoute from './components/ClinicModeRoute';
import DoctorRoute from './components/DoctorRoute';

// --- Páginas de Pacientes ---
import PatientDashboardPage from './pages/PatientDashboardPage';
import PatientAppointmentsPage from './pages/PatientAppointmentsPage.jsx';
import PatientProfilePage from './pages/PatientProfilePage';
import PatientMedicalRecordsPage from './pages/PatientMedicalRecordsPage';
import PatientDoctorsPage from './pages/PatientDoctorsPage';
import PatientReschedulePage from './pages/PatientReschedulePage';

// --- Páginas de Clínica ---
import AccountProfilePage from './pages/Account/AccountProfilePage';
import ChangePasswordPage from './pages/Account/ChangePasswordPage';
import ClinicProfilePage from './pages/Clinic/ClinicProfilePage';
import StaffProfilePage from './pages/Clinic/StaffProfilePage';
import ClinicSubscriptionPage from './pages/Clinic/ClinicSubscriptionPage';
import ClinicPlansPage from './pages/Clinic/ClinicPlansPage';
import SmartPlansRoute from './components/SmartPlansRoute';

// --- Otros Componentes ---
import VideoCallComponent from './components/VideoCallComponent';
import AIAssistant from './components/AIAssistant';

// Oculta el asistente en rutas públicas, telemedicina y portal de paciente
// Oculta el asistente en rutas públicas, telemedicina y portal de paciente
const AIAssistantWrapper = () => {
  const location = useLocation();
  
  // Normaliza trailing slash PERO preserva la raíz como '/'
  const raw = location.pathname;
  const path = raw === '/' ? '/' : raw.replace(/\/+$/, '');
  const qs = new URLSearchParams(location.search);

  // Rutas EXACTAS donde NO debe aparecer
  const HIDE_EXACT = new Set([
    '/',                    // ← Landing page (fix principal)
    '/login',
    '/register',
    '/forgot-password',
    '/doctors',
    '/thank-you',
    '/mockup-generator',
  ]);

  // Prefijos donde el asistente NO debe aparecer
  const HIDE_PREFIXES = [
    '/reset-password',
    '/patient',             // Todo el portal del paciente
    '/telemedicine',        // Telemedicina
    '/book',                // Booking público
    '/confirm-booking',     // Confirmación booking
    '/clinic/join',         // Join clínica
    '/google-auth-callback',
  ];

  const isHiddenByExact = HIDE_EXACT.has(path);
  const isHiddenByPrefix = HIDE_PREFIXES.some(p => path.startsWith(p));
  const hideByQuery = qs.get('hideAI') === '1';

  if (isHiddenByExact || isHiddenByPrefix || hideByQuery) {
    return null;
  }

  return <AIAssistant />;
};


// Redirecciona a /doctor + la ruta actual, conservando search y hash
const RedirectWithDoctorPrefix = () => {
  const location = useLocation();
  return (
    <Navigate
      to={`/doctor${location.pathname}${location.search}${location.hash}`}
      replace
    />
  );
};

function App() {
  return (
    <GuestProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ToastContainer position="top-right" autoClose={3000} />

        <Routes>
          {/* ===== RUTAS PÚBLICAS ===== */}
          {/* Autenticación */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/mockup-generator" element={<MockupGenerator />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
          <Route path="/google-auth-callback" element={<GoogleAuthCallback />} />
          <Route path="/clinic/join/:token" element={<ClinicJoinPage />} />

          {/* Autenticación de Pacientes */}
          <Route path="/patient/login" element={<PatientLoginPage />} />
          <Route path="/patient/register" element={<PatientRegisterPage />} />

          {/* Telemedicina y Reservas Públicas */}
          <Route path="/telemedicine/join/:roomId" element={<PatientJoinPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />

          {/* Booking: ruta inteligente PARA /book (pública o redirección a /patient) */}
          <Route path="/book/:doctorId" element={<SmartBookingRoute />} />
          {/* IMPORTANTE: eliminamos la ruta de nivel superior /patient/book/:doctorId
              para que el enrutado anidado dentro de PatientLayout tome prioridad y renderice en el Outlet */}

          {/* Confirmación de booking (si aplicas token) */}
          <Route path="/confirm-booking/:token" element={<ConfirmBookingPage />} />
          <Route path="/patient/confirm-booking/:token" element={<ConfirmBookingPage />} />

          {/* Buscar médicos (pública) */}
          <Route path="/doctors" element={<PatientDoctorsPage />} />

          {/* Redirección de la raíz a la página de login */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* ===== COMPATIBILIDAD: rutas de médico sin prefijo /doctor ===== */}
          <Route path="/dashboard" element={<Navigate to="/doctor/dashboard" replace />} />
          <Route path="/perfil/*" element={<RedirectWithDoctorPrefix />} />
          <Route path="/pacientes/*" element={<RedirectWithDoctorPrefix />} />
          <Route path="/citas/*" element={<RedirectWithDoctorPrefix />} />
          <Route path="/consultas/*" element={<RedirectWithDoctorPrefix />} />
          <Route path="/disponibilidad/*" element={<RedirectWithDoctorPrefix />} />
          <Route path="/configuracion-recordatorios/*" element={<RedirectWithDoctorPrefix />} />
          <Route path="/telemedicina/*" element={<RedirectWithDoctorPrefix />} />
          <Route path="/finanzas/*" element={<RedirectWithDoctorPrefix />} />
          <Route path="/catalogo/*" element={<RedirectWithDoctorPrefix />} />
          <Route path="/catalogo-medicamentos/*" element={<RedirectWithDoctorPrefix />} />
          <Route path="/ayuda/*" element={<RedirectWithDoctorPrefix />} />
          <Route path="/acerca-de/*" element={<RedirectWithDoctorPrefix />} />
          <Route path="/planes/*" element={<RedirectWithDoctorPrefix />} />
          <Route path="/suscripcion/*" element={<RedirectWithDoctorPrefix />} />
          <Route path="/opiniones/*" element={<RedirectWithDoctorPrefix />} />

          {/* ===== RUTAS PROTEGIDAS DE MÉDICOS ===== */}
          <Route
            path="/doctor"
            element={<ProtectedRoute><MainLayout /></ProtectedRoute>}
          >
            <Route
              path="clinica/miembros"
              element={
                <ClinicModeRoute>
                  <ClinicMembersPage />
                </ClinicModeRoute>
              }
            />
            {/* Dashboard y Estadísticas */}
            <Route path="dashboard" element={<DashboardPage />} />

            {/* Configurar Disponibilidad */}
            <Route path="disponibilidad" element={<DisponibilidadConfig />} />

            {/* Moderación de Opiniones */}
            <Route path="opiniones" element={<DoctorReviewsModerationPage />} />

            {/* Gestión de Citas */}
            <Route path="citas" element={<CitasPage />} />
            <Route path="citas/:id/edit" element={<EditCita />} />
            <Route path="configuracion-recordatorios" element={<ConfiguracionRecordatorios />} />

            {/* Gestión de Pacientes */}
            <Route path="pacientes" element={<PacientesPage />} />
            <Route path="pacientes/:id" element={<PacienteDetailPage />} />
            <Route path="pacientes/:id/crecimiento" element={<GrowthChartsPage />} />
            <Route path="pacientes/:id/edit" element={<EditPaciente />} />
            <Route path="pacientes/:id/nueva-consulta" element={<AddConsultaPage />} />

            {/* Consultas y Expedientes */}
            <Route path="consultas/:id" element={<ConsultaDetailPage />} />
            <Route path="consultas/:consultaId/edit/:id" element={<AddConsultaPage />} />

            {/* Telemedicina */}
            <Route path="telemedicina" element={<DoctorRoute><TelemedicinePage /></DoctorRoute>} />

            {/* Perfil y Configuración */}
            {/* Nueva estructura de Perfil */}
            <Route path="perfil">
              {/* Por defecto mostrar el perfil de la clínica */}
              <Route index element={<Navigate to="/doctor/perfil/medico" replace />} />
              {/* Perfil médico personal */}
              <Route path="medico" element={<MedicoProfilePage />} />
              {/* Perfil de la Clínica */}
              <Route path="personal" element={<StaffProfilePage />} />
              <Route path="clinica" element={<ClinicProfilePage />} />
            </Route>

            {/* Finanzas */}
            <Route path="finanzas" element={<FinanzasPage />} />

            {/* Catálogos */}
            <Route path="catalogo" element={<CatalogoPage />} />
            <Route path="catalogo-medicamentos" element={<MedicamentoCatalogoPage />} />

            {/* Ayuda y Soporte */}
            <Route path="ayuda" element={<AyudaPage />} />
            <Route path="acerca-de" element={<AcercaDePage />} />

            {/* Suscripción (usuario/médico) */}
            <Route path="planes" element={
              <SmartPlansRoute>
                <PlanesPage />
              </SmartPlansRoute>
            } />  
            <Route path="suscripcion" element={<SuscripcionPage />} />

            {/* NUEVO: Suscripción de Clínica (protegido + modo clínica) */}
            <Route
              path="clinica/suscripcion"
              element={
                <ClinicModeRoute>
                  <ClinicSubscriptionPage />
                </ClinicModeRoute>
              }
            />
            <Route
              path="clinica/planes"
              element={
                <ClinicModeRoute>
                  <ClinicPlansPage />
                </ClinicModeRoute>
              }
            />

            {/* Redirección por defecto */}
            <Route index element={<Navigate to="/doctor/dashboard" replace />} />
          </Route>

          {/* Compatibilidad / Aliases hacia las nuevas rutas de clínica */}
          <Route path="/suscripcion-clinica" element={<Navigate to="/doctor/clinica/suscripcion" replace />} />
          <Route path="/planes-clinica" element={<Navigate to="/doctor/clinica/planes" replace />} />

          {/* Clínicas (cuenta) */}
          <Route path="/account/perfil" element={<ProtectedRoute><AccountProfilePage /></ProtectedRoute>} />
          <Route path="/account/password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />

          {/* ===== RUTAS PROTEGIDAS DE PACIENTES (portal con layout) ===== */}
          <Route
            path="/patient"
            element={<PatientProtectedRoute><PatientLayout /></PatientProtectedRoute>}
          >
            {/* Dashboard del Paciente */}
            <Route path="dashboard" element={<PatientDashboardPage />} />

            {/* Gestión de Citas del Paciente */}
            <Route path="appointments" element={<PatientAppointmentsPage />} />
            <Route path="appointments/:id/reschedule" element={<PatientReschedulePage />} />

            {/* Buscar/Explorar médicos desde el portal del paciente */}
            <Route path="doctors" element={<PatientDoctorsPage />} />
            {/* Aquí se renderiza dentro del PatientLayout */}
            <Route path="book/:doctorId" element={<PublicBookingPage />} />
            <Route path="book" element={<Navigate to="/patient/doctors" replace />} />

            {/* Perfil del Paciente */}
            <Route path="profile" element={<PatientProfilePage />} />

            {/* Historial Médico */}
            <Route path="medical-records" element={<PatientMedicalRecordsPage />} />

            {/* Redirección por defecto */}
            <Route index element={<Navigate to="/patient/dashboard" replace />} />
          </Route>

          {/* Ruta para manejar URLs no encontradas */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>

        <AIAssistantWrapper />
      </LocalizationProvider>
    </GuestProvider>
  );
}

export default App;